import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { staffAPI } from '../services/api';
import { ChevronLeft, ChevronRight, Clock, AlertTriangle, CheckCircle, XCircle, MinusCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const AttendanceDaily = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [date, setDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [breakDrafts, setBreakDrafts] = useState({}); // attendanceId -> string the admin is typing
  const [savingBreakId, setSavingBreakId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await staffAPI.getDailyAttendance(date);
      setData(result);
      setBreakDrafts({});
    } catch (err) {
      setError('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveBreak = async (staff) => {
    if (!staff.attendanceId) return;
    const draft = breakDrafts[staff.attendanceId];
    if (draft === undefined) return; // user didn't touch the field

    // Empty string clears the override (back to auto). Otherwise parse a number.
    let parsed;
    if (draft.trim() === '') {
      parsed = null;
    } else {
      const n = Number(draft);
      if (!Number.isFinite(n) || n < 0 || n > 600) {
        setError('Break minutes must be between 0 and 600');
        return;
      }
      parsed = n;
    }

    // No-op if value didn't actually change
    const current = staff.breakMinutes === null || staff.breakMinutes === undefined
      ? null
      : Number(staff.breakMinutes);
    if (current === parsed) {
      setBreakDrafts(prev => {
        const next = { ...prev };
        delete next[staff.attendanceId];
        return next;
      });
      return;
    }

    try {
      setSavingBreakId(staff.attendanceId);
      setError('');
      await staffAPI.updateAttendance(staff.attendanceId, { breakMinutes: parsed });
      await fetchData();
    } catch (err) {
      setError('Failed to save break minutes');
    } finally {
      setSavingBreakId(null);
    }
  };

  const changeDate = (days) => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().split('T')[0]);
  };

  const formatDuration = (totalMinutes) => {
    const mins = Math.round(totalMinutes);
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (staff) => {
    const status = staff.attendanceStatus;
    const styles = {
      checked_in: { bg: '#dbeafe', color: '#1e40af', label: 'Checked In' },
      checked_out: { bg: '#d1fae5', color: '#065f46', label: 'Checked Out' },
      missed: { bg: '#fee2e2', color: '#991b1b', label: 'Missed' },
      pending: { bg: '#fffbeb', color: '#92400e', label: 'Pending' },
      upcoming: { bg: '#f3f4f6', color: '#6b7280', label: 'Upcoming' },
      not_scheduled: { bg: '#f3f4f6', color: '#6b7280', label: 'Not Scheduled' }
    };
    const s = styles[status] || styles.not_scheduled;
    return (
      <span style={{
        padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
        backgroundColor: s.bg, color: s.color
      }}>
        {s.label}
      </span>
    );
  };

  const getStatusIcon = (staff) => {
    switch (staff.attendanceStatus) {
      case 'checked_in': return <Clock size={20} color="#2563eb" />;
      case 'checked_out': return <CheckCircle size={20} color="#16a34a" />;
      case 'missed': return <XCircle size={20} color="#dc2626" />;
      case 'pending': return <Clock size={20} color="#d97706" />;
      case 'upcoming': return <MinusCircle size={20} color="#d1d5db" />;
      default: return <MinusCircle size={20} color="#9ca3af" />;
    }
  };

  const displayDate = new Date(date + 'T12:00:00').toLocaleDateString('en-AU', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
            <nav style={{ display: 'flex', gap: '20px' }}>
              <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Dashboard</button>
              <button onClick={() => navigate('/pallets')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Pallet Sheets</button>
              <button onClick={() => navigate('/reports')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Reports</button>
              {user?.role === 'admin' && (
                <button onClick={() => navigate('/admin')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#10b981', cursor: 'pointer', fontWeight: '600' }}>Admin</button>
              )}
            </nav>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>{user?.firstName} {user?.lastName}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
            </div>
            <button onClick={() => { logout(); navigate('/login'); }} style={{ padding: '8px 16px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Logout</button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '28px', color: '#111827' }}>Daily Attendance</h2>
            <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>View staff check-in/out times and photos</p>
          </div>
          <button
            onClick={() => navigate('/attendance/report')}
            style={{
              padding: '10px 20px', backgroundColor: '#f5f3ff', color: '#7c3aed',
              border: '1px solid #ddd6fe', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500'
            }}
          >
            Date Range Report
          </button>
        </div>

        {/* Date selector */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px',
          backgroundColor: 'white', padding: '12px 20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <button onClick={() => changeDate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: '#374151' }}>
            <ChevronLeft size={24} />
          </button>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: '600', fontSize: '18px', color: '#111827' }}>{displayDate}</div>
          </div>
          <button onClick={() => changeDate(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: '#374151' }}>
            <ChevronRight size={24} />
          </button>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            style={{ padding: '8px 12px', border: '2px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
          />
        </div>

        {error && (
          <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>Loading...</div>
        ) : data && data.staff.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '12px' }}>
            <p style={{ color: '#6b7280' }}>No active staff members found.</p>
          </div>
        ) : data && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
            {data.staff.map(staff => (
              <div key={staff.staffId} style={{
                backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                border: staff.isLate ? '2px solid #f59e0b' : (staff.attendanceStatus === 'missed' ? '2px solid #ef4444' : '2px solid transparent')
              }}>
                {/* Header */}
                <div style={{
                  padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {getStatusIcon(staff)}
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '16px', color: '#111827' }}>
                        {staff.firstName} {staff.lastName}
                      </div>
                      {staff.scheduled?.working && (
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Scheduled: {staff.scheduled.start} - {staff.scheduled.end}
                        </div>
                      )}
                    </div>
                  </div>
                  {getStatusBadge(staff)}
                </div>

                {/* Times */}
                {staff.attendanceStatus !== 'not_scheduled' && (
                  <div style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Check In</div>
                        <div style={{ fontSize: '17px', fontWeight: '600', color: staff.checkInTime ? '#111827' : '#d1d5db' }}>
                          {formatTime(staff.checkInTime)}
                        </div>
                        {staff.isLate && (
                          <div style={{ fontSize: '11px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                            <AlertTriangle size={12} /> {formatDuration(staff.lateMinutes)} late
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Check Out</div>
                        <div style={{ fontSize: '17px', fontWeight: '600', color: staff.checkOutTime ? '#111827' : '#d1d5db' }}>
                          {formatTime(staff.checkOutTime)}
                        </div>
                        {staff.isEarlyLeave && (
                          <div style={{ fontSize: '11px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                            <AlertTriangle size={12} /> {formatDuration(staff.earlyLeaveMinutes)} early
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Break (min)</div>
                        {staff.attendanceId ? (
                          <>
                            <input
                              type="number"
                              min="0"
                              max="600"
                              step="5"
                              inputMode="numeric"
                              disabled={!isAdmin || savingBreakId === staff.attendanceId}
                              value={
                                breakDrafts[staff.attendanceId] !== undefined
                                  ? breakDrafts[staff.attendanceId]
                                  : (staff.breakMinutes === null || staff.breakMinutes === undefined ? '' : String(staff.breakMinutes))
                              }
                              placeholder={`${staff.appliedBreakMinutes} (auto)`}
                              onChange={e => setBreakDrafts(prev => ({ ...prev, [staff.attendanceId]: e.target.value }))}
                              onBlur={() => saveBreak(staff)}
                              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                              style={{
                                width: '100%', padding: '6px 8px', fontSize: '15px', fontWeight: '600',
                                border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box',
                                backgroundColor: isAdmin ? 'white' : '#f9fafb', color: '#111827'
                              }}
                            />
                            {(staff.breakMinutes === null || staff.breakMinutes === undefined) && staff.checkOutTime && (
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                auto: {staff.appliedBreakMinutes}m
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{ fontSize: '15px', color: '#d1d5db' }}>—</div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Net Hours</div>
                        <div style={{ fontSize: '17px', fontWeight: '600', color: staff.checkOutTime ? '#111827' : '#d1d5db' }}>
                          {staff.checkOutTime ? formatDuration(staff.netWorkedMinutes) : '-'}
                        </div>
                        {staff.checkOutTime && staff.appliedBreakMinutes > 0 && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            {formatDuration(staff.workedMinutes)} gross
                          </div>
                        )}
                      </div>
                    </div>
                    {staff.averages && (
                      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #e5e7eb', display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280' }}>
                        <span>Avg in: {staff.averages.avgCheckIn}</span>
                        {staff.averages.avgCheckOut && <span>Avg out: {staff.averages.avgCheckOut}</span>}
                      </div>
                    )}
                  </div>
                )}

                {/* Photos */}
                {(staff.checkInPhoto || staff.checkOutPhoto) && (
                  <div style={{ padding: '0 16px 16px', display: 'flex', gap: '8px' }}>
                    {staff.checkInPhoto && (
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Check-in photo</div>
                        <img
                          src={`${API_BASE}${staff.checkInPhoto}`}
                          alt="Check-in"
                          style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                        />
                      </div>
                    )}
                    {staff.checkOutPhoto && (
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Check-out photo</div>
                        <img
                          src={`${API_BASE}${staff.checkOutPhoto}`}
                          alt="Check-out"
                          style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceDaily;
